/**
 * websiteAgentLayout — pure, reactive layout decisions for the website agent's
 * docked sidebar (FLO-519, part of FLO-514).
 *
 * Same contract as {@link ./demoLayout}: keep these PURE (viewport width in,
 * plain values out) so they are unit-testable with no DOM — WebsiteAgentView
 * itself can't be imported in the `node` test env (it pulls in `marked` and
 * `dompurify`, which expect a browser). Reactivity comes from the caller
 * re-rendering when {@link ../hooks/useViewportTier} crosses a breakpoint.
 *
 * Three tiers, because a 400px rail means very different things at different
 * widths:
 *
 *   ≥1024  'squeeze'    — rail beside the page; the host page gives up the strip
 *                         (see ../sdk/host-squeeze).
 *   768–1023 'overlay'  — rail on TOP of the page, no squeeze. Squeezing 400 out
 *                         of 1024 leaves the host ~624px, which breaks desktop
 *                         layouts (CSS `@media` can't see the squeeze, so the
 *                         host keeps rendering its desktop breakpoint into it).
 *   <768   'fullscreen' — the rail takes the screen, the standard mobile chat
 *                         pattern. A 400px rail on a 390px phone is not a rail.
 */

import type { CSSProperties } from 'react';
import type { VideoDimensions } from './demoLayout';

export type { VideoDimensions } from './demoLayout';

/** How the website agent renders. `floating` is the corner-anchored card. */
export type WebsiteAgentDisplayMode = 'floating' | 'sidebar';

/** Viewport tier for the sidebar. See the module docstring for the rationale. */
export type ViewportTier = 'squeeze' | 'overlay' | 'fullscreen';

/** Corner where a closed launcher docks. Bottom-right remains the safe default. */
export type LauncherPosition = 'bottom-right' | 'bottom-left';

/** At/above this width the host page is squeezed to make room for the rail. */
export const SQUEEZE_MIN_WIDTH_PX = 1024;

/** At/above this width the rail is a rail; below it, a full-screen takeover. */
export const RAIL_MIN_WIDTH_PX = 768;

/** Default rail width — Qualified parity. Mirrors the server-side default. */
export const DEFAULT_SIDEBAR_WIDTH_PX = 400;
/** Narrower than this and the chat composer + chips get cramped. */
export const MIN_SIDEBAR_WIDTH_PX = 320;
/** Wider than this and we're eating a marketing page, not docking beside it. */
export const MAX_SIDEBAR_WIDTH_PX = 480;

/**
 * Stacking order for the chat/rail. The live-demo stage uses the slot directly
 * below it, while the launcher and onboarding guidance remain above both.
 */
export const RAIL_Z = 2147483644;

/** The screencast sits beneath the stable chat/rail, but above the host page. */
export const DEMO_STAGE_Z = RAIL_Z - 1;

/**
 * Stacking order for the CLOSED state (launcher tile/pill + the chip row). The
 * slot the launcher pill has always used — above the rail, below the onboarding
 * guidance overlay (2147483647).
 */
export const LAUNCHER_Z = 2147483646;

/**
 * The resting launcher itself sits one slot BELOW its prompts. The tile's box is
 * wider than the presenter (a transparent frame around the figure), and the
 * prompts tuck into that frame to stand at the figure's shoulder — at an equal
 * z-index the later-rendered tile won, so the pills' arrows clicked through to
 * "open the agent" instead of asking their question. Still above the rail.
 */
export const LAUNCHER_TILE_Z = LAUNCHER_Z - 1;

/** One motion curve for every open/close move in sidebar mode. */
export const RAIL_TRANSITION = '0.3s ease-out';

/** The non-demo floating conversation grows out of the closed launcher corner. */
export const FLOATING_PANEL_WIDTH_PX = 420;
export const FLOATING_PANEL_HEIGHT_PX = 600;

/** The desktop speaking presenter. Unchanged by the viewport axes below. */
export const PRESENTER_SIZE_DESKTOP = { width: 256, height: 312 } as const;
/** The compact default: the mockup's frame, on any phone or tablet with room. */
export const PRESENTER_SIZE_COMPACT = { width: 160, height: 240 } as const;
/** Under 360px wide, where the clamped dock cannot pay for the full frame. */
export const PRESENTER_SIZE_COMPACT_NARROW = { width: 128, height: 192 } as const;
/** 480px tall or less — a phone turned sideways. The pre-mockup frame. */
export const PRESENTER_SIZE_COMPACT_SHORT = { width: 112, height: 132 } as const;

/**
 * The speaking presenter's frame, per tier and viewport.
 *
 * Every compact frame but the short one is the Anam stream's own 2:3, so a
 * `contain`-fitted portrait fills it edge to edge. The old 112×132 was 0.85,
 * which left the stream in an 88px strip with transparent gutters either side —
 * a face smaller than the frame that held it.
 *
 * The size is bought with BUBBLE width, per the mockup: the presenter grows and
 * the text lane beside it narrows. That lane is
 * `dock - presenter + overlap - compactBubblePaddingRight - COMPACT_BUBBLE_PADDING_X_PX`:
 *
 *   - a 375px phone, dock 343: `343 - 160 + 40 - 48 - 14` = 161px;
 *   - a 320px phone, dock 288, narrow presenter: `288 - 128 + 40 - 48 - 14` = 138px;
 *   - a tablet, dock 360: `360 - 160 + 40 - 48 - 14` = 178px.
 *
 * There is no floor below the narrow frame, and below 320px the lane does get
 * thin: a Galaxy Fold's 280px cover screen leaves `248 - 128 + 40 - 48 - 14` =
 * 98px, a ribbon. That is accepted rather than fixed — a fourth frame for a
 * viewport that narrow buys back a few px of lane and costs the face.
 *
 * The two shrinking axes are HEIGHT and WIDTH, not orientation. A phone held
 * sideways is 812–892px wide on anything modern, which is the OVERLAY tier, so
 * an orientation test aimed at `fullscreen` caught only an SE; what they all
 * share is 390–430px of height, against which 240 of presenter plus the bar's
 * own 56px row and the 16px inset leaves the bubble nothing. `short` therefore
 * wins over `narrow`: a sliver of a viewport that is both is height-bound
 * first. A tablet is neither, in either orientation, and keeps the full frame.
 *
 * `viewport` is one bag with both keys required — a half-filled object is a
 * type error rather than a silently wrong frame — and defaults whole, so the
 * chat's prop default and the dock tests get the mockup's size.
 */
export function compactAvatarPresenterSize(
  tier: ViewportTier,
  viewport: { short: boolean; narrow: boolean } = { short: false, narrow: false },
): Readonly<{ width: number; height: number }> {
  if (tier === 'squeeze') return PRESENTER_SIZE_DESKTOP;
  if (viewport.short) return PRESENTER_SIZE_COMPACT_SHORT;
  return viewport.narrow ? PRESENTER_SIZE_COMPACT_NARROW : PRESENTER_SIZE_COMPACT;
}

/**
 * Geometry for the ordinary floating Q&A panel.
 *
 * Desktop/tablet keeps the host page visible and interactive, with the panel
 * pinned to the exact corner occupied by the launcher. This preserves spatial
 * continuity: the thing a visitor clicked becomes the conversation. Phones use
 * the familiar full-screen chat treatment because there is no useful page area
 * left beside a 320px+ composer.
 */
export function floatingPanelLayout(
  tier: ViewportTier,
  position?: LauncherPosition,
  launcherInset?: number,
): CSSProperties {
  const side = resolveLauncherPosition(position);
  const inset = resolveLauncherInset(tier, launcherInset);
  const base: CSSProperties = {
    position: 'fixed',
    zIndex: RAIL_Z,
    pointerEvents: 'auto',
    display: 'flex',
    overflow: 'hidden',
    background: '#FFFFFF',
  };

  if (tier === 'fullscreen') {
    return {
      ...base,
      inset: 0,
      width: '100%',
      height: '100dvh',
      transformOrigin: side === 'bottom-left' ? 'bottom left' : 'bottom right',
    };
  }

  return {
    ...base,
    bottom: inset,
    ...(side === 'bottom-left' ? { left: inset } : { right: inset }),
    width: `min(${FLOATING_PANEL_WIDTH_PX}px, calc(100vw - ${2 * inset}px))`,
    height: `min(${FLOATING_PANEL_HEIGHT_PX}px, calc(100dvh - ${2 * inset}px))`,
    transformOrigin: side === 'bottom-left' ? 'bottom left' : 'bottom right',
  };
}

/**
 * Which tier a viewport width falls in. The single source of truth for the
 * breakpoints — {@link ../hooks/useViewportTier} derives its media queries from
 * the same two constants.
 */
export function viewportTier(width: number): ViewportTier {
  if (!Number.isFinite(width)) return 'squeeze'; // non-DOM/SSR → desktop default
  if (width >= SQUEEZE_MIN_WIDTH_PX) return 'squeeze';
  if (width >= RAIL_MIN_WIDTH_PX) return 'overlay';
  return 'fullscreen';
}

/** Only the widest tier gives up host-page width. */
export function shouldSqueeze(tier: ViewportTier): boolean {
  return tier === 'squeeze';
}

/** Viewport margin around the left demo panel, and its gap to the rail. */
export const DEMO_PANEL_MARGIN_PX = 16;
export const DEMO_PANEL_GAP_PX = 12;
/** Height of the avatar window pinned to the rail's top while a demo runs. */
export const RAIL_AVATAR_HEIGHT_PX = 190;

export interface DemoStageLayoutOptions {
  displayMode: WebsiteAgentDisplayMode;
  tier: ViewportTier;
  launcherPosition?: LauncherPosition;
  launcherInset?: number;
  sidebarWidth?: number;
  expanded?: boolean;
  /**
   * Width the anchored conversation actually occupies, when it is not the
   * ordinary Q&A panel. The avatar dock is wider than
   * {@link FLOATING_PANEL_WIDTH_PX}, and reserving the panel's width for it
   * lets the stage run underneath the presenter's bubble.
   */
  reservedChatWidth?: number;
  /**
   * The viewport is taller than it is wide. Only the overlay tier reads it: an
   * upright tablet is the one shape where the lane beside the dock is too
   * narrow for a 16:9 screencast but the column above it is not. See
   * {@link demoStageStacked}.
   */
  portrait?: boolean;
}

/**
 * Does the stage sit ABOVE the conversation rather than beside it?
 *
 * Exported so the stage, its header padding and the View all read one truth —
 * a stage that stacks while the header still pads for a dock beside it puts
 * the maximize button 360px from the edge of a card nothing is next to.
 */
export function demoStageStacked(opts: DemoStageLayoutOptions): boolean {
  return (
    opts.tier === 'overlay'
    && !!opts.portrait
    && !opts.expanded
    && opts.displayMode !== 'sidebar'
  );
}

/** Horizontal padding that keeps the demo header control at its resting x-position. */
export function demoStageHeaderRightPadding(opts: DemoStageLayoutOptions): number | string {
  const padding = 16;
  // A stacked stage is never `expanded` ({@link demoStageStacked}), so it takes
  // this path: the dock is BELOW the stage, not beside it, and the header has
  // nothing extra to clear. When a stacked stage IS maximized, the resting
  // geometry below is the stacked one, whose `right` is the plain margin — so
  // the button still does not move as the stage grows.
  if (!opts.expanded || opts.tier === 'fullscreen') return padding;

  const restingRight = demoStageLayout({ ...opts, expanded: false }).right;
  if (typeof restingRight === 'number') return restingRight + padding;
  if (typeof restingRight === 'string') return `calc(${padding}px + ${restingRight})`;
  return padding;
}

/**
 * Geometry for the live-demo screencast.
 *
 * The stage and conversation are independent siblings. At rest, the stage
 * fills the free side of the viewport opposite the anchored chat (or the space
 * left of a sidebar rail). Expanding changes only this geometry: the video grows
 * to the viewport beneath the chat, whose position and controls never move.
 * On phones there is no useful side-by-side space, so the stage already fills
 * the viewport behind the full-screen chat and is revealed when chat minimizes.
 */
export function demoStageLayout(opts: DemoStageLayoutOptions): CSSProperties {
  const base: CSSProperties = {
    position: 'fixed',
    zIndex: DEMO_STAGE_Z,
    pointerEvents: 'auto',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#0a0a0f',
  };

  if (opts.expanded || opts.tier === 'fullscreen') {
    return {
      ...base,
      inset: 0,
      borderRadius: 0,
      boxShadow: 'none',
    };
  }

  const framed: CSSProperties = {
    ...base,
    top: DEMO_PANEL_MARGIN_PX,
    bottom: DEMO_PANEL_MARGIN_PX,
    borderRadius: 18,
    boxShadow: '0 32px 80px -20px rgba(20,18,16,0.5), 0 4px 16px rgba(20,18,16,0.18)',
  };

  if (opts.displayMode === 'sidebar') {
    return {
      ...framed,
      left: DEMO_PANEL_MARGIN_PX,
      right: clampSidebarWidth(opts.sidebarWidth) + DEMO_PANEL_GAP_PX,
    };
  }

  // An upright tablet: full width at the TOP, with the conversation below it.
  //
  // Beside a 360px dock the lane on a 768px-wide viewport is 356px, and a 16:9
  // screencast `contain`-fitted into it is a 200px strip stranded in a ~950px
  // black column. Stacked, the same screencast is 736x414 — and the dock still
  // clears it, because the dock is bottom-anchored and
  // `compactDockHeight(PRESENTER_SIZE_COMPACT.height, 'overlay')` = 240 + 8 + 56
  // = 304px tall: 16 + header + 414 lands well above 1024 - 24 - 304.
  //
  // `bottom: 'auto'` is what makes that true: the card's height comes from its
  // content, and the video box below is a 16:9 element rather than a `flex: 1`
  // one ({@link demoStageVideoBoxStyle}), so the frame ends where the picture
  // does instead of painting black down to the margin.
  if (demoStageStacked(opts)) {
    return {
      ...framed,
      left: DEMO_PANEL_MARGIN_PX,
      right: DEMO_PANEL_MARGIN_PX,
      bottom: 'auto',
    };
  }

  const inset = resolveLauncherInset(opts.tier, opts.launcherInset);
  const reserved = opts.reservedChatWidth ?? FLOATING_PANEL_WIDTH_PX;
  const chatFootprint = `min(${reserved}px, calc(100vw - ${2 * inset}px))`;
  const reservedSide = `calc(${inset}px + ${chatFootprint} + ${DEMO_PANEL_GAP_PX}px)`;

  return resolveLauncherPosition(opts.launcherPosition) === 'bottom-left'
    ? { ...framed, left: reservedSide, right: DEMO_PANEL_MARGIN_PX }
    : { ...framed, left: DEMO_PANEL_MARGIN_PX, right: reservedSide };
}

/**
 * The box the screencast paints into, inside the stage's column.
 *
 * A stacked stage has no height of its own to divide up ({@link
 * demoStageLayout} gives it `bottom: 'auto'`), so the box states the
 * screencast's own 16:9 and the card ends where the picture does. Every other
 * stage fills a frame that already has a height, so the box takes what is left
 * under the header.
 */
export function demoStageVideoBoxStyle(stacked: boolean): CSSProperties {
  return stacked
    ? { position: 'relative', flex: 'none', aspectRatio: '16 / 9' }
    : { flex: 1, minHeight: 0, position: 'relative' };
}

/**
 * The screencast itself. `contain` always — a demoed app cropped to fill is a
 * demo of the wrong thing.
 *
 * On a PHONE the stage is the whole screen and the conversation is anchored to
 * its bottom, so a centred strip puts the demoed app directly under the bubble:
 * at 375x667 the picture landed at y 250..461 with 250px of unused black above
 * it. Pinning it to the top instead leaves that slack at the BOTTOM, where the
 * dock already is. This does not rescue a LANDSCAPE phone — there the strip
 * fills the box and the dock genuinely overlaps it — it only stops the
 * portrait case wasting the one gap it had.
 */
export function demoStageVideoStyle(tier: ViewportTier): CSSProperties {
  return {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
    background: 'transparent',
    objectPosition: tier === 'fullscreen' ? 'center top' : 'center',
  };
}

/**
 * Geometry for the OPEN rail.
 *
 * - squeeze / overlay: a fixed full-height column pinned to the right edge.
 *   `position: fixed` is anchored to the VIEWPORT, not to `<html>`, so the rail
 *   sits in the strip the squeeze opened up (and simply overlays the page in the
 *   overlay tier, where nothing was squeezed).
 * - fullscreen: the whole screen.
 *
 * `100dvh` (not `100vh`) so a mobile browser's dynamic toolbar doesn't push the
 * composer off-screen. No backdrop in any tier ≥768: the host page stays
 * interactive while the rail is open — that's the point of docking.
 */
export function railLayout(tier: ViewportTier, width: number): CSSProperties {
  const base: CSSProperties = {
    position: 'fixed',
    top: 0,
    right: 0,
    height: '100dvh',
    zIndex: RAIL_Z,
    pointerEvents: 'auto',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#FFFFFF',
  };
  if (tier === 'fullscreen') {
    return { ...base, left: 0, bottom: 0, width: '100%' };
  }
  return {
    ...base,
    width: clampSidebarWidth(width),
    // Only meaningful in the overlay tier (nothing was squeezed away, so the
    // rail visibly sits ON the page) — harmless once squeezed.
    boxShadow: '-18px 0 48px -24px rgba(20,18,16,0.45), -1px 0 0 rgba(20,18,16,0.08)',
  };
}

/**
 * Server/host config → a display mode we're willing to render. Anything that
 * isn't literally 'sidebar' falls back to today's floating card, so a typo or a
 * stale/garbage `websiteAgent.displayMode` can never take over a customer's
 * page layout.
 */
export function resolveDisplayMode(value: unknown): WebsiteAgentDisplayMode {
  return value === 'sidebar' ? 'sidebar' : 'floating';
}

/**
 * Server/host config → a usable rail width. Absent/junk → the 400px default;
 * out of range → clamped to 320–480 (floe-api clamps the same range on write,
 * this is the belt for older/hand-edited config blobs).
 */
export function clampSidebarWidth(value: unknown): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : NaN;
  if (Number.isNaN(n)) return DEFAULT_SIDEBAR_WIDTH_PX;
  return Math.min(MAX_SIDEBAR_WIDTH_PX, Math.max(MIN_SIDEBAR_WIDTH_PX, n));
}

// ───────────────────────────────────────────────────────────────────────────
// FLO-520 — the CLOSED state: corner launcher + variant-aware prompt stack
// ───────────────────────────────────────────────────────────────────────────

/**
 * The closed-state launcher renders one of two things:
 *   'tile' — an Anam-style portrait video card playing the site's silent idle
 *            loop, with an "Ask {agent}" pill overlaid at its bottom.
 *   'pill' — the compact "Ask {agent}" pill (today's launcher, re-anchored).
 * The Video avatar switch is applied first; this helper derives the variant
 * from the remaining enabled media.
 */
export type LauncherVariant = 'tile' | 'pill';

/**
 * Avatar media from the resolved `websiteAgent.avatar` facet. Both fields are
 * optional on purpose — an older floe-api build serves an avatar facet without
 * them, and the launcher must degrade rather than throw.
 */
export interface AvatarMedia {
  /** Short, silent, seamless loop of the presenter. Absent → no video tile. */
  idleLoopUrl?: string;
  /** Still frame for reduced motion / slow network / refused autoplay. */
  posterUrl?: string;
  /** The approach intro, when the avatar ships a usable one. */
  intro?: AvatarIntroMedia;
}

/**
 * The approach intro (floe-api `AvatarIntroFiles`): at rest the presenter
 * stands a few steps back, walks up on the click while the live session
 * connects, and walks back when the call ends.
 *
 * Present only when it is USABLE — the far loop, its poster, the approach AND
 * the close loop the approach lands on. Everything that consumes it can then
 * test one field instead of re-deriving that rule.
 */
export interface AvatarIntroMedia {
  /** Still of the far pose. */
  farPosterUrl: string;
  /** Looping far idle clip: what the launcher tile plays at rest. */
  farIdleLoopUrl: string;
  /** One-shot: far pose → the close loop's first frame. */
  approachUrl: string;
  /** One-shot: the approach reversed, for the end of a call. */
  retreatUrl?: string;
  /** One-shot far-pose gestures, in the order they are cycled. */
  gestureUrls: string[];
}

/** At most this many gestures are honoured; a config blob is not trusted to be short. */
export const AVATAR_INTRO_MAX_GESTURES = 8;

/**
 * One operator-supplied media URL, or undefined. Mirrors floe-api's
 * `pickMediaUrl` (website-agent.config.ts) on purpose: the server already
 * whitelists the scheme, and this is the belt for an older/hand-edited config
 * blob reaching a newer SDK. Only absolute http(s) survives — `javascript:`,
 * `data:` (unbounded payload), `blob:` and protocol-relative `//evil.tld` all
 * fall out, and the launcher degrades to the pill rather than dropping a
 * half-trusted URL into a `<video src>` on the customer's page.
 */
function pickMediaUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const raw = value.trim();
  if (!raw) return undefined;
  try {
    const { protocol } = new URL(raw);
    return protocol === 'https:' || protocol === 'http:' ? raw : undefined;
  } catch {
    return undefined; // relative/garbage — `new URL` throws without a base
  }
}

/** Resolve the raw avatar facet (any shape, possibly absent) to usable media. */
export function resolveAvatarMedia(raw: unknown): AvatarMedia {
  const rec = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
  const idleLoopUrl = pickMediaUrl(rec?.idleLoopUrl);
  const intro = resolveAvatarIntro(rec?.intro, idleLoopUrl);
  return {
    idleLoopUrl,
    posterUrl: pickMediaUrl(rec?.posterUrl),
    ...(intro ? { intro } : {}),
  };
}

/**
 * The intro, or undefined unless every clip the sequence cannot do without
 * survived the URL check. A partial intro is worse than none: a far loop with
 * no approach strands the presenter at the back of the frame on the click.
 */
function resolveAvatarIntro(raw: unknown, idleLoopUrl: string | undefined): AvatarIntroMedia | undefined {
  if (!idleLoopUrl || !raw || typeof raw !== 'object') return undefined;
  const rec = raw as Record<string, unknown>;
  const farPosterUrl = pickMediaUrl(rec.farPosterUrl);
  const farIdleLoopUrl = pickMediaUrl(rec.farIdleLoopUrl);
  const approachUrl = pickMediaUrl(rec.approachUrl);
  if (!farPosterUrl || !farIdleLoopUrl || !approachUrl) return undefined;
  const retreatUrl = pickMediaUrl(rec.retreatUrl);
  const gestureUrls = (Array.isArray(rec.gestureUrls) ? rec.gestureUrls : [])
    .map(pickMediaUrl)
    .filter((url): url is string => !!url)
    .slice(0, AVATAR_INTRO_MAX_GESTURES);
  return { farPosterUrl, farIdleLoopUrl, approachUrl, ...(retreatUrl ? { retreatUrl } : {}), gestureUrls };
}

/**
 * Which launcher to render. The truth table:
 *
 *   no idle loop and no poster      → 'pill'  (nothing to show)
 *   poster only                     → 'tile'  (still cut-out presenter)
 *   idle loop, video errored/timed out → 'pill'  (degrade, never a dead box)
 *   idle loop, reduced motion, no poster → 'pill'  (nothing static to show —
 *       a non-autoplaying <video> paints a blank rectangle, not a first frame)
 *   idle loop, otherwise            → 'tile'
 *
 * Both variants share {@link launcherAnchor}, so degrading never moves anything.
 */
export function launcherVariant(
  media: AvatarMedia | undefined,
  opts: { videoFailed?: boolean; reducedMotion?: boolean } = {},
): LauncherVariant {
  const loop = media?.idleLoopUrl?.trim();
  const poster = media?.posterUrl?.trim();
  if (!loop && !poster) return 'pill';
  if (opts.videoFailed) return 'pill';
  if (opts.reducedMotion && !poster) return 'pill';
  return 'tile';
}

/** Portrait tile geometry — desktop (squeeze tier) and everything below it. */
export const LAUNCHER_TILE_DESKTOP = { width: 240, height: 320 } as const;
export const LAUNCHER_TILE_COMPACT = { width: 120, height: 160 } as const;

/**
 * Resting-tile overlay geometry for the COMPACT tiers.
 *
 * The tile carries two bottom-anchored overlays: the centred "Talk to me" CTA
 * and the keyboard button pinned to the corner. On the desktop tile they clear
 * each other; on {@link LAUNCHER_TILE_COMPACT} they did not — the 44px button
 * covered the CTA's last third and, at z-index 3 over the CTA's 1, swallowed
 * the taps aimed at it, so the primary VOICE action opened text mode.
 *
 * Lifting the button out of the CTA's band beats capping the CTA: a cap tight
 * enough to clear a 44px corner button (120 - 44 - 8 = 68px) would ellipsize
 * "Talk to me" on every compact tile.
 *
 * The height budget is the CTA's TALLEST line box, not its Latin one:
 * LAUNCHER_FONT falls through to Noto Sans Devanagari for Hindi, whose line box
 * runs ~3px past the Latin ~29px, and the CTA is `white-space: nowrap`, so it
 * grows in height rather than wrapping.
 */
export const LAUNCHER_TILE_CTA_BOTTOM_PX = 8;
export const LAUNCHER_TILE_CTA_BOTTOM_COMPACT_PX = 4;
export const LAUNCHER_TILE_CTA_HEIGHT_BUDGET_COMPACT_PX = 32;
export const LAUNCHER_TILE_KEYBOARD_SIZE_PX = 44;
export const LAUNCHER_TILE_KEYBOARD_GAP_PX = 8;
/**
 * Derived, never typed twice: the keyboard button's bottom offset is whatever
 * puts its lower edge a clear gap above the CTA's upper edge.
 */
export const LAUNCHER_TILE_KEYBOARD_BOTTOM_COMPACT_PX =
  LAUNCHER_TILE_CTA_BOTTOM_COMPACT_PX +
  LAUNCHER_TILE_CTA_HEIGHT_BUDGET_COMPACT_PX +
  LAUNCHER_TILE_KEYBOARD_GAP_PX;
/**
 * The same lift for the DESKTOP tile, which needs one as soon as the button is
 * permanently visible there. A coarse-pointer device at the squeeze tier — a
 * touch laptop, an iPad with a trackpad at ≥1024px — now enters the touch
 * reveal block and gets the button at the stylesheet's resting `bottom: 4px`,
 * which is inside the CTA's band. The compact tiers lift via the inline style
 * ({@link LAUNCHER_TILE_KEYBOARD_BOTTOM_COMPACT_PX}); the desktop tier lifts
 * here, in that media block, so a mouse-only desktop is untouched.
 */
export const LAUNCHER_TILE_KEYBOARD_BOTTOM_TOUCH_PX =
  LAUNCHER_TILE_CTA_BOTTOM_PX +
  LAUNCHER_TILE_CTA_HEIGHT_BUDGET_COMPACT_PX +
  LAUNCHER_TILE_KEYBOARD_GAP_PX;

/**
 * The PILL launcher's keyboard button. 44px is DESIGN.md's minimum touch
 * target — it was 40 while the button was hover-only, and the touch reveal is
 * what makes the minimum apply.
 */
export const PILL_KEYBOARD_SIZE_PX = 44;
export const PILL_KEYBOARD_RIGHT_PX = 9;
export const PILL_KEYBOARD_GAP_PX = 8;
/**
 * Padding the RESTING pill reserves on touch, where the button is always
 * visible: the prompt copy has to stop this far short of the edge or it runs
 * underneath it. Derived so it cannot drift from the button it clears.
 */
export const PILL_KEYBOARD_TOUCH_GUTTER_PX =
  PILL_KEYBOARD_RIGHT_PX + PILL_KEYBOARD_SIZE_PX + PILL_KEYBOARD_GAP_PX;

/**
 * The widest the launcher may ever be, in ANY variant. The tile is fixed at
 * {@link launcherTileSize}, but the PILL is content-sized ("Ask {siteName}"), so
 * without a cap a long site name would grow it past the tile footprint that
 * {@link chipRowLayout} reserves — and the rightmost nudge chip would render
 * underneath it. The pill consumes this as its `max-width` and the chip row
 * reserves exactly this much, so the two can never disagree.
 */
export const LAUNCHER_MAX_WIDTH_PX = 360;

/** Default distance from the viewport edge. */
export const LAUNCHER_INSET_PX = 24;
export const LAUNCHER_INSET_MOBILE_PX = 16;

/** Gap between the launcher and the chip row it must never sit under. */
const LAUNCHER_CHIP_GAP_PX = 12;

/**
 * How far the keyed figure sits inside the live-video tile's side edges. The
 * tile is a portrait frame and the presenter only fills its middle: every
 * catalog avatar is recorded hips-up with the head at ~44% (CLAUDE.md, "Avatar
 * catalog assets"), which leaves ~52px of transparent frame either side of the
 * figure on the 240px desktop tile. Laying the prompt lane against the tile's
 * box therefore parked the pills ~64px from the presenter; tucking the lane by
 * this much puts them ~20px from the figure — beside the presenter, not beside
 * the empty edge of its frame. A constant, because the catalog shares one
 * framing; per-avatar bounds are the follow-up (docs/designs/website-agent-host-ask.md §B).
 */
export const LAUNCHER_TILE_FIGURE_INSET_PX = 44;
export const LAUNCHER_TILE_FIGURE_INSET_COMPACT_PX = 20;

/** The figure inset for a tier's tile ({@link launcherTileSize}). */
export const launcherTileFigureInset = (tier: ViewportTier): number =>
  tier === 'squeeze' ? LAUNCHER_TILE_FIGURE_INSET_PX : LAUNCHER_TILE_FIGURE_INSET_COMPACT_PX;

/** Expanded opener height plus its gap — the base of the corner prompt stack. */
export const LAUNCHER_PILL_STACK_OFFSET_PX = 82;
/** Vertical gap between contextual nudge chips. */
export const LAUNCHER_NUDGE_GAP_PX = 8;
/** Maximum two-line nudge height (64px) plus its inter-chip gap. */
export const LAUNCHER_NUDGE_STACK_STEP_PX = 64 + LAUNCHER_NUDGE_GAP_PX;
/**
 * How far above the viewport inset the tile's prompt lane rests, so its lowest
 * chip sits level with the tile's "Talk to me" pill (bottom: 8px inside the
 * tile at desktop size) instead of with the tile's bottom edge.
 */
export const LAUNCHER_TILE_LANE_BOTTOM_OFFSET_PX = 8;
/** No-scroll dwell before the scaled launcher returns to its resting size. */
export const LAUNCHER_SCROLL_IDLE_MS = 5000;
/** One-shot delay before useLauncherIdlePresentation reveals contextual nudges. */
export const LAUNCHER_NUDGE_REVEAL_MS = 3800;
/** How long the post-call booking action accompanies the restored launcher. */
export const POST_CALL_BOOKING_OFFER_MS = 10_000;
/** Stable-section dwell before scrollspy swaps contextual nudge copy. */
export const LAUNCHER_NUDGE_SCROLLSPY_DWELL_MS = 700;

/**
 * Height occupied by the visible nudge surface for launcher-adjacent controls.
 * Both variants now stack their prompts in a column ({@link chipRowLayout}), so
 * the height is the same formula for each; the parameter stays so a caller
 * never has to know that.
 */
export function launcherNudgeSurfaceHeight(_variant: LauncherVariant, count: number): number {
  const visibleCount = Math.max(0, Math.floor(count));
  if (visibleCount === 0) return 0;
  return visibleCount * LAUNCHER_NUDGE_STACK_STEP_PX - LAUNCHER_NUDGE_GAP_PX;
}

/**
 * Tile size for a tier. Only the squeeze tier (≥1024px — a real desktop) gets
 * the full 240×320 card: below that the tile and its bottom-center prompt row
 * share a tighter viewport, so a 240px-wide tile starves it (see
 * {@link chipRowLayout}). The compact 120×160 tile is also the spec'd mobile
 * size, so phones and tablets land on the same card.
 */
export function launcherTileSize(tier: ViewportTier): { width: number; height: number } {
  return tier === 'squeeze' ? { ...LAUNCHER_TILE_DESKTOP } : { ...LAUNCHER_TILE_COMPACT };
}

/**
 * Resolve config input at the render edge: older SDKs and hand-edited embeds
 * can pass arbitrary values, but only the two documented corners are valid.
 */
export function resolveLauncherPosition(value: unknown): LauncherPosition {
  return value === 'bottom-left' ? 'bottom-left' : 'bottom-right';
}

/** Resolve a safe viewport inset, retaining the established per-tier defaults. */
export function resolveLauncherInset(tier: ViewportTier, value?: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.round(value));
  return tier === 'fullscreen' ? LAUNCHER_INSET_MOBILE_PX : LAUNCHER_INSET_PX;
}

/**
 * The corner anchor. BOTH variants spread this, which is what makes the
 * video→pill degrade jump-free: the corner, the inset and the stacking slot are
 * identical, so only the card's own box changes.
 */
export function launcherAnchor(
  tier: ViewportTier,
  position?: LauncherPosition,
  launcherInset?: number,
): CSSProperties {
  const inset = resolveLauncherInset(tier, launcherInset);
  return {
    position: 'fixed',
    bottom: inset,
    ...(resolveLauncherPosition(position) === 'bottom-left' ? { left: inset } : { right: inset }),
    zIndex: LAUNCHER_TILE_Z,
    pointerEvents: 'auto',
  };
}

/** Two choices keep the closed state scannable instead of becoming a menu. */
export const MAX_CHIP_ROW = 2;
export const MAX_CHIP_ROW_MOBILE = 2;
/** Below this the row is hidden entirely — two chips would be shreds of text. */
export const CHIP_ROW_MIN_WIDTH_PX = 380;

/**
 * How many prompt chips may show at a given viewport width. Two keeps the
 * closed state scannable in either a corner stack (pill launcher) or the
 * bottom-center live-video row. 0 (very narrow) hides the prompts while the
 * launcher remains available.
 *
 * The cap is applied BEFORE the SHOWN telemetry effect, so a chip that never
 * renders never reports an impression (the event name and payload are
 * unchanged — only chips the visitor could actually see are counted).
 */
export function chipRowCapacity(width: number): number {
  if (!Number.isFinite(width)) return MAX_CHIP_ROW; // SSR / no DOM → desktop
  if (width >= CHIP_ROW_MIN_WIDTH_PX) return MAX_CHIP_ROW;
  return 0;
}

/**
 * Prompt-container geometry for each launcher variant.
 *
 * Pill launcher: prompts stack directly above the configured corner, keeping
 * their relationship to the launcher obvious and avoiding page-level chrome.
 *
 * Live-video tile: prompts stack in the lane BESIDE the presenter, bottom-aligned
 * with its voice pill and growing upward — the same lane the avatar dock's
 * bubble occupies once a call starts, so a chip the visitor taps is replaced by
 * the answer in the exact place they were looking. The old bottom-centre row
 * put the prompts half a viewport away from the face they belong to, and on a
 * page with its own bottom-left CTAs they read as host chrome.
 */
export function chipRowLayout(
  tier: ViewportTier,
  launcherInset?: number,
  variant: LauncherVariant = 'tile',
  position?: LauncherPosition,
): CSSProperties {
  const tile = launcherTileSize(tier);
  const inset = resolveLauncherInset(tier, launcherInset);
  const side = resolveLauncherPosition(position);

  const stack: CSSProperties = {
    position: 'fixed',
    zIndex: LAUNCHER_Z,
    pointerEvents: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: side === 'bottom-left' ? 'flex-start' : 'flex-end',
    gap: LAUNCHER_NUDGE_GAP_PX,
    // The overlay bundles no global box-sizing reset, so host pages vary —
    // pin it or the max-width stops meaning what it says.
    boxSizing: 'border-box',
  };

  // Without live video, the prompts belong to the launcher, not to the page.
  // Stack them directly above its corner so the relationship is unambiguous.
  if (variant === 'pill') {
    return {
      ...stack,
      bottom: inset + LAUNCHER_PILL_STACK_OFFSET_PX,
      ...(side === 'bottom-left' ? { left: inset } : { right: inset }),
      width: `min(${LAUNCHER_MAX_WIDTH_PX}px, calc(100vw - ${2 * inset}px))`,
      maxWidth: `calc(100vw - ${2 * inset}px)`,
    };
  }

  // The lane beside the tile: from the viewport edge opposite the launcher up
  // to the presenter's near edge (the tile's edge, tucked by the transparent
  // frame around the figure), minus a gap. Capped at the launcher's own maximum
  // width so two prompts never become a banner across a wide desktop.
  const laneEdge = inset + tile.width + LAUNCHER_CHIP_GAP_PX - launcherTileFigureInset(tier);
  return {
    ...stack,
    bottom: inset + LAUNCHER_TILE_LANE_BOTTOM_OFFSET_PX,
    ...(side === 'bottom-left' ? { left: laneEdge } : { right: laneEdge }),
    width: `min(${LAUNCHER_MAX_WIDTH_PX}px, calc(100vw - ${laneEdge + inset}px))`,
    maxWidth: `calc(100vw - ${laneEdge + inset}px)`,
  };
}

// ─── Anchored presenter ──────────────────────────────────────────────────────
// A host page can mark elements `data-floe-presenter-anchor` (precedent:
// `data-floe-dock-target`). While the agent is dormant, the desktop tier shows
// the live-video tile, and ALL of an anchor is on screen, the resting presenter
// stands in it, larger, instead of in the corner. Between two anchors it holds
// still and steps into the next as it arrives (stepPresenterAnchor); with no
// anchor on screen it goes back to the corner.
// An attribute rather than a `Floe()` option, so there is no config key for
// convertFloeToSDKConfig's explicit map to drop.

export const PRESENTER_ANCHOR_ATTR = 'data-floe-presenter-anchor';
/** Never smaller than the corner tile's width reads as a deliberate placement. */
export const PRESENTER_ANCHOR_MIN_WIDTH_PX = 280;
/**
 * The idle loop is recorded at 768px wide (REQUIRED_LOOP_GEOMETRY), so 384 CSS px
 * is the widest tile a 2x screen shows without upscaling. floe.so stands its
 * presenter at ~27% of the viewport (owner, 2026-09-25), so the ceiling accepts
 * up to ~15% upscaling on a 2x screen at 440px: presence over the last pixel.
 */
export const PRESENTER_ANCHOR_MAX_WIDTH_PX = 440;
/**
 * How much of the anchor must be on screen for the presenter to stand in it:
 * effectively all of it (the slack absorbs subpixel layout). A partial rule let
 * a tall screen anchor the presenter below the fold at page load, its "Talk to
 * me" control cut off, and kept it anchored with its head scrolled out of view
 * on the way out. Partly visible means the corner, never half a presenter.
 */
export const PRESENTER_ANCHOR_VISIBLE_FRACTION = 0.98;
/** Corner ⇄ anchor handoff duration. */
export const PRESENTER_HANDOFF_MS = 450;

/**
 * The tile box inside a host anchor rect: the tile's 3:4 aspect, width clamped
 * to [MIN, MAX], aligned to the anchor's bottom-right (where a presenter standing
 * beside something would put its feet).
 */
export const anchoredPresenterBox = (rect: PresenterBox): PresenterBox => {
  const aspect = LAUNCHER_TILE_DESKTOP.width / LAUNCHER_TILE_DESKTOP.height;
  const fit = Math.min(rect.width, rect.height * aspect);
  const width = Math.round(Math.min(PRESENTER_ANCHOR_MAX_WIDTH_PX, Math.max(PRESENTER_ANCHOR_MIN_WIDTH_PX, fit)));
  const height = Math.round(width / aspect);
  return {
    height,
    left: Math.round(rect.left + rect.width - width),
    top: Math.round(rect.top + rect.height - height),
    width,
  };
};

/** Fraction (0..1) of the rect's height that is inside the viewport. */
export const anchorVisibleFraction = (rect: PresenterBox, viewportHeight: number): number => {
  if (!(rect.height > 0)) {
    return 0;
  }
  const visible = Math.min(rect.top + rect.height, viewportHeight) - Math.max(rect.top, 0);
  return Math.min(1, Math.max(0, visible / rect.height));
};

/**
 * Where the resting presenter stands, given every host anchor's rect in document
 * order: the first anchor that is laid out and wholly on screen, or null for the
 * corner. A page can mark several (floe.so: its story frame and its closing band);
 * they are far enough apart that at most one is ever fully visible.
 */
export const pickPresenterAnchor = (rects: PresenterBox[], viewportHeight: number): PresenterBox | null => {
  const rect = rects.find(
    (r) => r.width > 0 && anchorVisibleFraction(r, viewportHeight) >= PRESENTER_ANCHOR_VISIBLE_FRACTION,
  );
  return rect ? anchoredPresenterBox(rect) : null;
};

/**
 * The resting presenter's placement between measures. `box` is where it stands
 * (null: the corner). `index` is the anchor it stands in; while `holding` it
 * stands still at `box` between two anchors, having left anchor `from`. `tops`
 * and `scrollY` are the previous measure, which tell an anchor that is still
 * travelling from one that has arrived.
 */
export interface PresenterAnchorTrack {
  box: PresenterBox | null;
  from: number;
  holding: boolean;
  index: number;
  scrollY: number;
  tops: number[];
}

export const PRESENTER_TRACK_START: PresenterAnchorTrack = {
  box: null,
  from: -1,
  holding: false,
  index: -1,
  scrollY: 0,
  tops: [],
};

/** Within this many px, a travelling anchor has reached the held presenter. */
const PRESENTER_ARRIVE_PX = 2;

/**
 * One measure of the presenter's walk between host anchors.
 *
 * It stands in an anchor while all of it is on screen and moves with it, as
 * pickPresenterAnchor — unless the page is scrolling that anchor away while
 * another is coming into view. Then it stops where it stands and holds, and steps
 * into the next anchor once that one has arrived: reached the held spot, crossed
 * it, stopped moving while the page scrolls (a pinned story frame), or scrolling
 * has `settled` (an anchor coming down from above can stop short of the held
 * spot at the top of the page); or once the anchor it left is gone. It steps back
 * into the anchor it left only where that anchor meets it again. With no anchor
 * on screen at all it goes to the corner.
 *
 * On floe.so the hero's anchor leaves ~160px of scroll before the story's is
 * whole. The corner rule dropped the presenter to the corner and back up; riding
 * the hero's anchor out took it to the top edge, prompts cut off, and then down.
 */
export const stepPresenterAnchor = (
  prev: PresenterAnchorTrack,
  rects: PresenterBox[],
  viewportHeight: number,
  scrollY: number,
  settled = false,
): PresenterAnchorTrack => {
  const boxes = rects.map((r) => (r.width > 0 ? anchoredPresenterBox(r) : null));
  const seen = rects.map((r) => (r.width > 0 ? anchorVisibleFraction(r, viewportHeight) : 0));
  const tops = boxes.map((b) => (b ? b.top : Number.NaN));
  const scrolled = scrollY !== prev.scrollY;
  const whole = (i: number) => i >= 0 && seen[i] >= PRESENTER_ANCHOR_VISIBLE_FRACTION;
  const travelled = (i: number) => (Number.isFinite(prev.tops[i]) ? Math.abs(tops[i] - prev.tops[i]) : 0);
  const stand = (i: number): PresenterAnchorTrack => ({
    box: boxes[i],
    from: -1,
    holding: false,
    index: i,
    scrollY,
    tops,
  });
  const hold = (box: PresenterBox, from: number): PresenterAnchorTrack => ({
    box,
    from,
    holding: true,
    index: -1,
    scrollY,
    tops,
  });
  if (!prev.holding && whole(prev.index)) {
    const going = scrolled && travelled(prev.index) >= 1 && seen.some((f, i) => i !== prev.index && f > 0);
    return going && prev.box ? hold(prev.box, prev.index) : stand(prev.index);
  }
  const left = prev.holding ? prev.from : prev.index;
  const held = prev.box;
  if (left >= 0 && held) {
    const arrived = (i: number) => {
      const was = prev.tops[i];
      const now = tops[i];
      const met =
        Math.abs(now - held.top) <= PRESENTER_ARRIVE_PX ||
        (Number.isFinite(was) && (was - held.top) * (now - held.top) < 0);
      if (i === left) {
        return met;
      }
      return met || settled || seen[left] === 0 || (scrolled && Number.isFinite(was) && travelled(i) < 1);
    };
    const next = boxes.findIndex((b, i) => b !== null && whole(i) && arrived(i));
    if (next !== -1) {
      return stand(next);
    }
    if (seen.some((f) => f > 0)) {
      return hold(held, left);
    }
  }
  const first = boxes.findIndex((b, i) => b !== null && whole(i));
  return first === -1 ? { ...PRESENTER_TRACK_START, scrollY, tops } : stand(first);
};

/**
 * The resting corner tile as a box in viewport coordinates, so the handoff can
 * transition top/left/width/height between the corner and the anchor (a switch
 * between `bottom/right` and `top/left` would jump instead of animating).
 */
export const cornerPresenterBox = (
  tier: ViewportTier,
  viewport: { height: number; width: number },
  position?: LauncherPosition,
  launcherInset?: number,
): PresenterBox => {
  const tile = launcherTileSize(tier);
  const inset = resolveLauncherInset(tier, launcherInset);
  return {
    height: tile.height,
    left: resolveLauncherPosition(position) === 'bottom-left' ? inset : viewport.width - inset - tile.width,
    top: viewport.height - inset - tile.height,
    width: tile.width,
  };
};

/**
 * The empty band above the presenter's head, as a fraction of the tile height.
 * Anam lands every face at the same place in the 768x1152 portrait frame
 * (hair starts ~22% down), so this holds for any catalog avatar; the prompts
 * drop into it rather than floating a whole headroom above the head.
 */
export const LAUNCHER_TILE_HEADROOM_FRACTION = 0.16;

/**
 * Prompts while the presenter is anchored: stacked just above its head (inside
 * the tile's empty headroom, painted over the tile — see LAUNCHER_TILE_Z) and
 * aligned to the figure's right edge. Beside it (the corner lane) would put them
 * over the host content the anchor sits next to.
 */
export const anchoredChipRowLayout = (
  box: PresenterBox,
  viewport: { height: number; width: number },
): CSSProperties => {
  const figureInset = Math.round((box.width * LAUNCHER_TILE_FIGURE_INSET_PX) / LAUNCHER_TILE_DESKTOP.width);
  return {
    alignItems: 'flex-end',
    bottom: Math.round(viewport.height - box.top - box.height * LAUNCHER_TILE_HEADROOM_FRACTION),
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: LAUNCHER_NUDGE_GAP_PX,
    pointerEvents: 'auto',
    position: 'fixed',
    right: Math.round(viewport.width - (box.left + box.width) + figureInset),
    width: `min(${LAUNCHER_MAX_WIDTH_PX}px, ${box.width + 2 * figureInset}px)`,
    zIndex: LAUNCHER_Z,
  };
};

/**
 * What the enabled chat's empty-state avatar hero should paint. The live stream
 * wins among enabled sources; the configured idle loop is the launcher asset, so
 * the tile visually "becomes" the panel's avatar card when it opens (and a real
 * avatar stream slots into the exact same slot later — that's why the caller
 * passes media in rather than the component hardcoding a source).
 *
 * 'none' means the avatar is disabled or has no renderable source.
 */
export type AvatarHeroSource = 'none' | 'stream' | 'loop' | 'poster' | 'logo' | 'letter';

export function avatarHeroSource(opts: {
  hasStream?: boolean;
  media?: AvatarMedia;
  avatarEnabled?: boolean;
  hasLogo?: boolean;
  reducedMotion?: boolean;
}): AvatarHeroSource {
  const loop = opts.media?.idleLoopUrl?.trim();
  const poster = opts.media?.posterUrl?.trim();
  // The operator's video-avatar switch owns every face surface. Stored media is
  // retained for a later re-enable, but must not leak into the launcher/chat
  // while the avatar is disabled.
  if (!opts.avatarEnabled) return 'none';
  if (opts.hasStream) return 'stream';
  if (loop && !opts.reducedMotion) return 'loop';
  if (poster) return 'poster';
  // Reduced motion with no still frame: same call as the launcher makes — a
  // non-autoplaying <video> is a blank rectangle, so use the branded fallback.
  if (opts.hasLogo) return 'logo';
  return 'letter';
}

/**
 * Does the walk-up hold the live stream off screen right now?
 *
 * Only while the approach is actually being PLAYED, and exactly one surface
 * plays it: the compact dock's idle-loop slot. The expanded card shows the
 * idle loop instead and reports no `ended`, and a loop that failed has fallen
 * back to a poster — holding a ready stream behind either would keep the
 * presenter's face still while the welcome is already being spoken, until the
 * backstop gave up.
 */
export const approachHoldsStream = (opts: {
  approachPlaying: boolean;
  dockVisible: boolean;
  restingSource: AvatarHeroSource;
}): boolean => opts.approachPlaying && opts.dockVisible && opts.restingSource === 'loop';

/**
 * How long a launcher video gets to prove itself before we fall back to the
 * pill. Cleared by the first `loadedmetadata`/`canplay` — so this only fires
 * for a URL that never resolves (dead CDN, blocked host, offline).
 */
export const LAUNCHER_VIDEO_TIMEOUT_MS = 5000;

/** A box in viewport coordinates — the subset of DOMRect the intro FLIP reads. */
export interface PresenterBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Every avatar clip and the live track are 2:3, drawn `object-fit: contain`. */
const PRESENTER_ASPECT = 2 / 3;

/** Where a contained 2:3 figure actually lands inside the box that holds it. */
function containedFigure(box: PresenterBox): PresenterBox {
  const width = Math.min(box.width, box.height * PRESENTER_ASPECT);
  const height = width / PRESENTER_ASPECT;
  return {
    left: box.left + (box.width - width) / 2,
    top: box.top + (box.height - height) / 2,
    width,
    height,
  };
}

/**
 * How long the presenter takes to travel between the resting tile's geometry
 * and the dock's. A little under the ~2.5s retreat clip it runs beside, so the
 * box has settled by the time the clip lands on its last frame. The approach
 * is played slower and travels for {@link APPROACH_FLIP_MS} instead.
 */
export const INTRO_FLIP_MS = 2200;

/**
 * How long the approach is PLAYED for: the ~2.5s clip, slowed.
 *
 * The walk-up covers the wait for the call. Prod, 2026-10-02 (44 floe.so
 * sessions): the first word arrived 7.6s after the click at the median, the
 * clip was over at 2.5s, and the presenter then stood in the close pose — the
 * pose that says "ready to talk" — for 5.0s (p90 6.8s) with nothing said. A
 * longer walk spends more of that wait on motion and less on a silent face.
 * Not longer than this: the clip is 25fps, and past ~0.6x the slow-down shows
 * (see `MIN_STRETCH_PLAYBACK_RATE` in AvatarVideo).
 */
export const APPROACH_PLAY_MS = 3700;

/** The length the intro clips are generated at (`make_avatar_intro.py`). */
const INTRO_CLIP_MS = 2500;

/**
 * The approach's travel between the two boxes: the same share of the played
 * clip that {@link INTRO_FLIP_MS} is of the unslowed one, so the box still
 * settles just before the presenter does.
 */
export const APPROACH_FLIP_MS = Math.round((INTRO_FLIP_MS / INTRO_CLIP_MS) * APPROACH_PLAY_MS);

export interface PresenterFlip {
  /** Uniform scale that makes the TO figure the size of the FROM figure. */
  scale: number;
  dx: number;
  dy: number;
  /** transform-origin, in px relative to the TO box: the figure's feet. */
  originX: number;
  originY: number;
}

/**
 * The transform that makes the presenter in its NEW box look exactly like it
 * did in its OLD one — the first keyframe of a FLIP back to `none`.
 *
 * The resting tile and the docked presenter are different boxes. On desktop
 * that is 213x320 → 208x312 and nobody sees it; below the squeeze tier it is
 * 107x160 → 160x240 and 64px higher (the call bar sits beneath the presenter),
 * so without this the presenter JUMPS 1.5x bigger on the click and only then
 * starts to walk closer — and jumps back down at the end of the call. Both
 * boxes draw a contained 2:3 figure, so the comparison is between the FIGURES,
 * anchored at the feet, not between the boxes that hold them.
 *
 * Measured from the DOM rather than derived from the tier constants: launcher
 * position, inset, the narrow/short presenter sizes and the bar's height all
 * move these boxes, and a formula would have to track every one of them.
 *
 * `null` when there is nothing to animate: an unmeasurable box, or a change
 * too small to see.
 */
export function presenterFlip(from: PresenterBox | null | undefined, to: PresenterBox | null | undefined): PresenterFlip | null {
  if (!from || !to || !(from.width > 0 && from.height > 0 && to.width > 0 && to.height > 0)) return null;
  const a = containedFigure(from);
  const b = containedFigure(to);
  const scale = a.height / b.height;
  const feet = (fig: PresenterBox) => ({ x: fig.left + fig.width / 2, y: fig.top + fig.height });
  const dx = feet(a).x - feet(b).x;
  const dy = feet(a).y - feet(b).y;
  if (!Number.isFinite(scale) || scale <= 0) return null;
  if (Math.abs(scale - 1) < 0.01 && Math.abs(dx) < 1 && Math.abs(dy) < 1) return null;
  return { scale, dx, dy, originX: feet(b).x - to.left, originY: feet(b).y - to.top };
}

/**
 * Longest the dock may stay in the approach before handing over to the close
 * loop regardless. The approach plays for {@link APPROACH_PLAY_MS}; this is
 * the backstop for one that never reports `ended` (autoplay refused, a stalled
 * decode), without which the presenter would hold the far pose for the whole
 * call — and, since the live stream waits for the approach, hold the stream
 * off screen with it. So it tracks the played length, with slack for a clip
 * that is slow to start: a stalled walk must not keep a ready presenter hidden
 * for seconds after the walk should have ended.
 */
export const APPROACH_MAX_MS = APPROACH_PLAY_MS + 1300;

/**
 * FLO-545 review: where the LIVE avatar stream renders inside the chat card.
 *
 * The hero above owns the EMPTY state only (WebsiteAgentChat gates it on
 * `!hasConversation`), so the first message unmounts it — and with it went the
 * card's only <video> for the stream, leaving a live, metered Anam session
 * running invisibly for the rest of the conversation. The stream therefore
 * moves to a compact strip docked above the messages.
 *
 * FLO-545 originally refused that slot to static media, on the rule that the
 * persistent slot exists for the one source that BILLS while unseen. The idle
 * swap changes the premise rather than the rule: when the client retires the
 * live track after a silent stretch it is the idle loop, not the stream, that
 * has to hold the slot — otherwise the presenter disappears mid-conversation
 * and the card reflows around the hole.
 *
 * Be clear-eyed about the cost of that. An idled session is still billing, and
 * now it is billing behind a loop, which is precisely the invisibility FLO-545
 * set out to prevent. The idle swap is a rendering change with no effect on
 * spend (see `sdk/avatar-idle.ts`); only ending the Anam session server-side
 * stops the meter, and that is not something this layer can do.
 *
 *   nothing to show          → 'none'   (a media-less chat is untouched)
 *   ended                    → 'none'   (the ended card replaces the chat body)
 *   media, no messages       → 'hero'   (unchanged empty-state behaviour)
 *   media, conversation      → 'strip'
 */
export type ChatAvatarSlot = 'none' | 'hero' | 'strip';

export function chatAvatarSlot(opts: {
  hasStream?: boolean;
  /** An idle loop or poster is configured, so a retired stream has a stand-in. */
  hasIdleMedia?: boolean;
  hasConversation?: boolean;
  ended?: boolean;
}): ChatAvatarSlot {
  if (opts.ended) return 'none';
  if (!opts.hasStream && !opts.hasIdleMedia) return 'none';
  return opts.hasConversation ? 'strip' : 'hero';
}

/**
 * Height of the in-conversation live-avatar strip. Deliberately more compact
 * than {@link RAIL_AVATAR_HEIGHT_PX} (the rail-top window while a demo runs):
 * the strip shares its column with the transcript AND the composer, so it must
 * read as a face keeping the visitor company, not a surface competing with the
 * conversation for the rail's height.
 */
export const CHAT_AVATAR_STRIP_HEIGHT_PX = 120;

/**
 * FLO-545 review: geometry for the video tile INSIDE the strip.
 *
 * The strip used to stretch the video across the card's full width at
 * {@link CHAT_AVATAR_STRIP_HEIGHT_PX} — `cover` then crops the 768×1152
 * portrait Anam stream to a thin horizontal band of the face. Same cure as
 * demoLayout's `avatarPiPFrame`: the tile adopts the stream's OWN ratio once
 * metadata reports it (16:9 until then — the shape the old full-width box
 * approximated), pinned to the strip's fixed height and centered by the auto
 * margins, so `cover` has nothing left to crop. `maxWidth` keeps an
 * ultra-wide stream inside the card; when it binds, `cover` crops
 * symmetrically — the degraded case, never the portrait one.
 */
export function chatAvatarStripFrame(dims: VideoDimensions | null | undefined): CSSProperties {
  const valid =
    dims && Number.isFinite(dims.width) && Number.isFinite(dims.height) && dims.width > 0 && dims.height > 0
      ? dims
      : null;
  const w = valid ? valid.width : 16;
  const h = valid ? valid.height : 9;
  return {
    height: CHAT_AVATAR_STRIP_HEIGHT_PX,
    aspectRatio: `${w} / ${h}`,
    maxWidth: '100%',
    margin: '0 auto',
  };
}

// ───────────────────────────────────────────────────────────────────────────
// FLO-754 — the AVATAR compact dock: one presenter, one bubble, one bar.
//
// Everything below applies ONLY when a video avatar is actually renderable
// (stream, idle loop or poster). A site without one keeps the pre-existing
// compact dock unchanged — see `compactAvatarVisible` in WebsiteAgentChat.
//
// The model: the presenter is never inside a container. It holds the right of
// the dock at a fixed size with the bubble to its left, and it overlaps the
// BUBBLE's right end, so the two read as one object rather than a card with a
// face in it. The bar is not in that lane: it rides at the presenter's feet or
// takes a row of its own beneath the pair, per `compactDockBarPlacement`.
// Two consequences the geometry has to respect:
//
//   1. the overlapped strip is DEAD SPACE for anything with text or a control
//      — only the bubble's empty right shoulder may run under the presenter;
//   2. the presenter's own frame must stop being a rectangle, or the character
//      is sliced off wherever the body reaches an edge.
// ───────────────────────────────────────────────────────────────────────────

/**
 * Width of the avatar dock on the SQUEEZE tier. Wider than
 * {@link FLOATING_PANEL_WIDTH_PX} on purpose — it is sized to what this tier
 * actually ships:
 *
 *   - a 256px presenter ({@link compactAvatarPresenterSize});
 *   - a typing pill of `presenter + COMPACT_DOCK_TYPING_OVERHANG_PX` = 352px,
 *     pinned to the presenter's outer edge ({@link compactTypingBarWidth});
 *   - a bubble lane whose text column, `500 - 256 + 52 - 60 - 14` = 222px, is
 *     wide enough to read rather than a word-per-line ribbon.
 *
 * Every other tier goes through {@link avatarDockWidth} — this number was sized
 * for the 256px presenter, not the smaller ones the compact tiers render.
 */
export const AVATAR_DOCK_WIDTH_PX = 500;

/** Dock width on BOTH compact tiers. See {@link avatarDockWidth}. */
export const AVATAR_DOCK_WIDTH_COMPACT_PX = 360;

/**
 * How much width the docked call reserves, per tier.
 *
 * One dock width for both compact tiers, whatever presenter
 * {@link compactAvatarPresenterSize} picks for the viewport. Beside the widest
 * of them (160px) the bubble's text column is `360 - 160 + 40 - 48 - 14` =
 * 178px, and 161px on a 375px phone where the dock clamps to 343. Narrower than
 * the 222px the desktop dock leaves beside its 256px presenter: below squeeze
 * the bigger face is bought with bubble width, per the mockup.
 *
 * The dock is not only its own box: it is what {@link demoStageLayout} and the
 * booking stage reserve as `reservedChatWidth`, so its width is subtracted from
 * whatever runs beside it. On the overlay tier the old number made a compact
 * presenter cost the stage 500px — at 768 the calendar iframe was left
 * `768 - 16 - (24 + 500 + 12)` = 216px, narrower than any booking widget
 * renders usefully. It now keeps 356.
 *
 * Phones ≤ 392px are unaffected: the dock is already clamped to
 * `100vw - 2 * inset`, which binds first there. Above that, a fullscreen
 * viewport (a 744px iPad mini, a phone held sideways) now gets the same 360 as
 * the tablet beside it, rather than a dock that was WIDER on the narrower
 * device.
 */
export function avatarDockWidth(tier: ViewportTier): number {
  return tier === 'squeeze' ? AVATAR_DOCK_WIDTH_PX : AVATAR_DOCK_WIDTH_COMPACT_PX;
}

/**
 * How far the presenter overlaps the bubble/bar column. Wide enough that the
 * bubble's edge disappears behind the presenter — which is what makes the two
 * read as one object — and narrow enough to leave a usable column at every tier.
 */
export const COMPACT_PRESENTER_OVERLAP_PX = 52;
export const COMPACT_PRESENTER_OVERLAP_COMPACT_PX = 40;

/** Clearance between the presenter's left edge and any text or control. */
export const COMPACT_PRESENTER_CLEARANCE_PX = 8;

/**
 * Alpha ramp at the presenter's frame edges, as a percentage of the frame.
 * The bottom needs more than the sides: a talking head fills the lower frame
 * completely, so a short ramp there still reads as a cut.
 */
export const AVATAR_EDGE_FADE_BOTTOM_PCT = 16;
export const AVATAR_EDGE_FADE_SIDE_PCT = 9;

/**
 * One feathered ramp: opaque for most of its length, then a fast, blurred
 * roll-off in the last third. A LINEAR ramp of the same length is half
 * transparent at its midpoint — that made the presenter's whole lower torso
 * ghostly (the hoodie logo at 50% alpha) while the frame edge itself was still
 * visible. Docket's Frameless presenter reads better because it keeps the body
 * solid and spends its alpha on a short, soft edge; this is that curve.
 */
function featheredRamp(direction: string, lengthPct: number): string {
  const at = (fraction: number) => `${(lengthPct * fraction).toFixed(2)}%`;
  return [
    `linear-gradient(${direction}`,
    'transparent 0',
    `rgba(0,0,0,0.18) ${at(0.25)}`,
    `rgba(0,0,0,0.55) ${at(0.5)}`,
    `rgba(0,0,0,0.88) ${at(0.75)}`,
    `#000 ${at(1)})`,
  ].join(', ');
}

export function compactPresenterOverlap(tier: ViewportTier): number {
  return tier === 'squeeze'
    ? COMPACT_PRESENTER_OVERLAP_PX
    : COMPACT_PRESENTER_OVERLAP_COMPACT_PX;
}

/**
 * Right padding that keeps bubble TEXT out of the presenter's column while the
 * bubble's own box still runs underneath it.
 */
export function compactBubblePaddingRight(tier: ViewportTier): number {
  return compactPresenterOverlap(tier) + COMPACT_PRESENTER_CLEARANCE_PX;
}

/**
 * Pixel blur at the presenter's edges, rendered INSIDE the chroma-key shader
 * (AvatarVideo `edgeBlur`): the blur ramps over the bottom `span` of the
 * frame's height and the outer `sideSpan` of its width, compounding in the
 * bottom corners, and reaches `radius` (a fraction of the height) at the edge.
 * Kept SHORT and SMALL on purpose: a wide, heavy blur read as a low-bitrate
 * video rather than a soft edge (2026-09-11, first cut was 0.28 / 0.03).
 * The alpha feather in {@link avatarEdgeFade} makes the frame edge disappear,
 * but the pixels that remain were still sharp, so the torso ended in a crisp
 * band. A `backdrop-filter` strip was tried first and rejected: it blurs the
 * PAGE behind the frame too, and its own rectangle showed on the host page.
 */
export const AVATAR_EDGE_BLUR = { span: 0.16, radius: 0.016, sideSpan: 0.08 } as const;

/**
 * The presenter's frame treatment.
 *
 * The chroma key already produces real alpha and even feathers hair and
 * shoulders against the backdrop — what it cannot soften is the edge of the
 * FRAME. Without this the keyed video is an opaque rectangle: the body meets
 * the frame and stops on a straight line, which on a customer's page is the
 * first thing a visitor sees. `mask-composite` intersects three ramps so a
 * body that reaches the bottom AND a side fades at both.
 *
 * Returned as plain CSS (both the standard and `-webkit-` spellings) rather
 * than applied inside AvatarVideo, because only the FREE-STANDING surfaces
 * want it — a framed PiP inside a card should keep its hard edge.
 */
export function avatarEdgeFade(): CSSProperties {
  const ramps = [
    featheredRamp('to top', AVATAR_EDGE_FADE_BOTTOM_PCT),
    featheredRamp('to right', AVATAR_EDGE_FADE_SIDE_PCT),
    featheredRamp('to left', AVATAR_EDGE_FADE_SIDE_PCT),
  ].join(', ');
  return {
    WebkitMaskImage: ramps,
    WebkitMaskComposite: 'source-in',
    maskImage: ramps,
    maskComposite: 'intersect',
  } as CSSProperties;
}

/**
 * Which configuration the dock's bar is in.
 *
 *   'voice'    — mic, end, open. The default for a voice start, and the whole
 *                point of the redesign: no text input under a live call.
 *   'controls' — adds type-instead and mute-replies. Five is the ceiling; a
 *                sixth control runs under the presenter.
 *   'typing'   — the bar BECOMES the composer. The only state with a text
 *                field, one tap in and one tap back.
 */
export type CompactDockBar = 'voice' | 'controls' | 'typing';

/**
 * Where the dock's bar sits.
 *
 *   'feet'  — absolutely positioned inside the presenter, centred at its feet:
 *             the spot the launcher's "Talk to me" pill just vacated.
 *   'below' — a row of its own, under the [bubble | presenter] pair.
 *
 * The bar is sized by its CONTROLS and the presenter by the tier, and only the
 * squeeze tier's 256px presenter is wider than its own five-control bar. No
 * compact one is: 254px of pill centred on those feet reaches across the
 * bubble's link row on one side and past the viewport edge on the other (at
 * 375px the fifth control lands entirely off-screen). So below the squeeze
 * tier the bar leaves the feet and takes its own row, where the dock's full
 * width is available to it.
 */
export type CompactDockBarPlacement = 'feet' | 'below';

export function compactDockBarPlacement(tier: ViewportTier): CompactDockBarPlacement {
  return tier === 'squeeze' ? 'feet' : 'below';
}

/** Gap between the [bubble | presenter] pair and a bar row placed below it. */
export const COMPACT_DOCK_BAR_ROW_GAP_PX = 8;

/**
 * Height of the bar pill as the JSX paints it: one control tall, plus the bar's
 * own padding and its border on each side. The controls sit in a ROW, so the
 * pill is as tall as one of them, however many there are.
 */
export function compactDockBarHeight(controlSize: number): number {
  return controlSize + 2 * COMPACT_DOCK_BAR_PADDING_PX + 2 * COMPACT_DOCK_BAR_BORDER_PX;
}

/**
 * How tall the docked call is, for anything that has to clear it.
 *
 * At the 'feet' placement the bar is inside the presenter's footprint, so the
 * dock is the frame. 'below' adds its own row: the gap plus the pill, which is
 * 44 + 2·5 + 2·1 = 56 at the 44px touch target the compact tiers use.
 *
 * Nothing renders through this: it is a MODEL of the painted dock, and what it
 * exists for is the stage-clearance tests — it is not a runtime guard, and no
 * layout consults it before positioning anything. Its value is that it reads
 * the same constants the JSX does, so a presenter that changes size cannot
 * leave a "the dock is about N px tall" comment behind it. That is not
 * hypothetical: the number in {@link demoStageLayout} was written for a 112px
 * presenter and was 33px short the moment the frame grew.
 *
 * Both the placement and the control size come from the TIER, for the same
 * reason {@link compactAvatarPresenterSize} and {@link avatarDockWidth} do — a
 * model that hardcodes one tier's numbers is a model of nothing in particular.
 */
export function compactDockHeight(presenterHeight: number, tier: ViewportTier): number {
  if (compactDockBarPlacement(tier) === 'feet') return presenterHeight;
  return (
    presenterHeight
    + COMPACT_DOCK_BAR_ROW_GAP_PX
    + compactDockBarHeight(compactDockControlSize(tier))
  );
}

/**
 * Style for the bar's row wrapper — the one thing the placement moves.
 *
 * The bar is rendered ONCE, in one position in the tree, and changes place by
 * style alone. It used to be rendered at two sites and re-parent across the
 * tier boundary, which unmounts it, and an unmount is not recoverable from a
 * React effect: a programmatic `focus()` outside a user gesture does not
 * reopen the iOS soft keyboard, and an in-flight IME composition — every CJK
 * visitor mid-word — is destroyed with the element. So the tree must not
 * change shape, whatever the tier does.
 *
 *   'feet'  — the wrapper IS the presenter's footprint, absolutely placed over
 *             its feet (the dock root is the containing block, and the
 *             presenter holds the dock's right edge, so `right: 0` plus the
 *             presenter's width lands exactly on it). Voice/controls centre
 *             the pill in it. The typing pill is WIDER than the wrapper, and
 *             `flex-end` keeps its right edge on the presenter's outer edge
 *             while it overflows into the lane beside it.
 *   'below' — a row in normal flow under the pair, right-aligned so the pill
 *             stays under the face it belongs to.
 */
export function compactDockBarRowStyle(
  placement: CompactDockBarPlacement,
  bar: CompactDockBar,
  presenterWidth: number,
): CSSProperties {
  if (placement === 'below') {
    return { display: 'flex', justifyContent: 'flex-end', marginTop: COMPACT_DOCK_BAR_ROW_GAP_PX };
  }
  return {
    position: 'absolute',
    bottom: 8,
    right: 0,
    width: presenterWidth,
    display: 'flex',
    justifyContent: bar === 'typing' ? 'flex-end' : 'center',
    zIndex: 3,
    // The wrapper covers the presenter's feet; only the pill inside it takes
    // pointer events, or this box would eat clicks aimed at the face.
    pointerEvents: 'none',
  };
}

/**
 * Control size in the dock's bar. 44px is the touch target on every tier that
 * is plausibly touched; the squeeze tier trades it for a tighter bar beside a
 * 256px presenter.
 */
export function compactDockControlSize(tier: ViewportTier): number {
  return tier === 'squeeze' ? 36 : 44;
}

/**
 * The bar pill's inner geometry, which the JSX renders from directly.
 * {@link compactDockBarWidth} below is a model of the pill built from these
 * same exported constants, so a fit assertion is measuring the pill that
 * actually paints — it is not itself what paints.
 */
export const COMPACT_DOCK_BAR_GAP_PX = 6;
export const COMPACT_DOCK_BAR_PADDING_PX = 5;
export const COMPACT_DOCK_BAR_BORDER_PX = 1;
/** Five is the ceiling: mic, end, collapse, type-instead, mute-replies. */
export const COMPACT_DOCK_MAX_CONTROLS = 5;

/**
 * Width of the bar pill as the JSX will paint it: its controls, the gaps
 * between them, its own padding and its border on each side. Nothing renders
 * through this — it is the arithmetic the fit checks need, kept honest by
 * reading the same constants the JSX does.
 */
export function compactDockBarWidth(controlSize: number, controls: number): number {
  return (
    controls * controlSize +
    (controls - 1) * COMPACT_DOCK_BAR_GAP_PX +
    2 * COMPACT_DOCK_BAR_PADDING_PX +
    2 * COMPACT_DOCK_BAR_BORDER_PX
  );
}

/**
 * How far the TYPING pill grows past the presenter into the lane beside it, on
 * the 'feet' placement. The pill pins to the presenter's outer edge, so this is
 * the only direction it can take room from — and the dock's width is what keeps
 * the far end on screen.
 */
export const COMPACT_DOCK_TYPING_OVERHANG_PX = 96;

/** Rendered width of the typing pill at the presenter's feet. */
export function compactTypingBarWidth(presenterWidth: number): number {
  return presenterWidth + COMPACT_DOCK_TYPING_OVERHANG_PX;
}

/** The bubble's left padding — its text column starts here. */
export const COMPACT_BUBBLE_PADDING_X_PX = 14;

/** Ceiling on the desktop bubble; the compact tiers use the presenter's own height. */
export const DESKTOP_BUBBLE_CAP_PX = 240;

/**
 * Ceiling on the bubble's height, given the tallest it may ever be (`capPx`).
 * A long answer must scroll inside the bubble, not grow it off the top of the
 * viewport: the dock is bottom-anchored, so an unbounded bubble pushes its own
 * beginning off screen.
 *
 * The live bubble passes the PRESENTER's height LESS its own lift
 * ({@link compactBubbleLift}), because the lift holds the bubble's bottom clear
 * of the presenter's feet: capped at the bare frame height, a full bubble tops
 * out a lift's worth ABOVE the face rather than level with it. With the usual
 * 12px lift that is 228 / 180 / 120 for the three compact frames. The desktop
 * dock passes 312 and is held at {@link DESKTOP_BUBBLE_CAP_PX} instead: its
 * bubble never grew past 240 and the phone change is no reason to start — which
 * is also why the 64px typing lift, which only ever applies at the 'feet'
 * placement, changes nothing there. The default is the compact frame for
 * callers that pass nothing.
 *
 * The transcript bubble asks for LESS than the face, never more: its flat 208
 * stood proud of the narrow (192) and short (132) frames, so it is floored
 * against the same cap rather than added to it.
 *
 * Squeeze keeps the flat px cap; the compact tiers, where the bar has its own
 * row and the viewport is small, also cap against the viewport.
 *
 * That cap is `vh`, NOT `dvh`, unlike {@link railLayout}: this is a React
 * inline style, which cannot carry a fallback declaration the way a stylesheet
 * can, so a browser that does not parse `dvh` drops the whole `max-height` and
 * the bubble grows unbounded — the exact failure the cap exists to prevent.
 * `vh` over-reads by the mobile toolbar's height, which the px ceiling absorbs
 * on a portrait phone and which costs a few px in landscape.
 */
export function compactBubbleMaxHeight(
  tier: ViewportTier,
  capPx: number = PRESENTER_SIZE_COMPACT.height,
): number | string {
  // Never negative. The caller subtracts a lift from a frame height, and a
  // frame shorter than its own lift would emit `min(-12px, 36vh)` — which the
  // CSS parser DROPS, taking the whole max-height with it and leaving the
  // bubble unbounded: the exact failure the cap exists to prevent, arrived at
  // through the cap.
  const safeCapPx = Math.max(0, capPx);
  // The desktop bubble keeps its 240px ceiling beside the 312px presenter:
  // raising it was never asked for, and a taller bubble there is a desktop
  // change hidden inside a phone fix.
  return tier === 'squeeze'
    ? Math.min(safeCapPx, DESKTOP_BUBBLE_CAP_PX)
    : `min(${safeCapPx}px, 36vh)`;
}

/**
 * Bottom padding under the bubble column. The typing pill is wider than the
 * presenter and overhangs the lane beside it, so the bubble lifts to clear it;
 * otherwise it sits low, level with the presenter's chest. A bar placed
 * 'below' overhangs nothing, so it never lifts.
 */
export function compactBubbleLift(
  placement: CompactDockBarPlacement,
  bar: CompactDockBar,
): number {
  return placement === 'feet' && bar === 'typing' ? 64 : 12;
}
